import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../api';
import { LocationDtoV3 } from '../../generated-api';
import { useQueryOfflineModeEnabled } from './useFeatureFlagApi';
import { useGetLocationsForCurrentUserV2 } from '@ordr-as/ordr-service-types/epos';
import { isValidLocationId } from '../utils/locationId';

const LOCATION_ID_CACHE_KEY = 'favrit.epos.locationIdMapping';

function getCachedNumericId(locationId: string): number | undefined {
  try {
    const cached = localStorage.getItem(`${LOCATION_ID_CACHE_KEY}.${locationId}`);
    if (!cached) return undefined;
    const parsed = parseInt(cached, 10);
    // Validate: must be a positive integer (handles NaN, 0, negative)
    return parsed > 0 ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function setCachedNumericId(locationId: string, numericId: number) {
  try {
    localStorage.setItem(`${LOCATION_ID_CACHE_KEY}.${locationId}`, numericId.toString());
  } catch {
    // Ignore storage errors
  }
}

export function useCachedLocationId(locationId?: string): number | undefined {
  return locationId ? getCachedNumericId(locationId) : undefined;
}

export function useNumericLocationId(locationId?: string, location?: LocationDtoV3): number | undefined {
  const cachedNumericId = useCachedLocationId(locationId);
  return location?.id ?? cachedNumericId;
}

export function useQueryLocations() {
  const { queryKey: _queryKey, ...result } = useGetLocationsForCurrentUserV2({
    query: {
      staleTime: 10 * 60 * 1000, // 10 min
    },
  });
  return result;
}

const getOptimisticLocation = (useCache: boolean, data: LocationDtoV3, cachedData?: LocationDtoV3): LocationDtoV3 => {
  if (!useCache || !cachedData) {
    return data;
  }

  const missingPlacements = cachedData.placements.length && !data?.placements.length;
  const missingPaymentMethods = cachedData.paymentMethods.length > data?.paymentMethods.length;
  const missingDiscounts = cachedData.discounts.length && !data?.discounts.length;

  // if parts of the response from server is missing content, fallback on cached data (check missing)
  return {
    ...data,
    placements: missingPlacements ? cachedData.placements : data.placements,
    paymentMethods: missingPaymentMethods ? cachedData.paymentMethods : data.paymentMethods,
    discounts: missingDiscounts ? cachedData.discounts : data.discounts,
  };
};

type OfflineModeOptions = {
  meta: { persist: boolean };
  enabled: boolean;
};

const useOfflineMode = (id?: string): OfflineModeOptions => {
  const { data: offlineModeFeatureFlag } = useQueryOfflineModeEnabled(id);

  return {
    meta: { persist: isValidLocationId(id) && !!offlineModeFeatureFlag?.enabled },
    enabled: !!offlineModeFeatureFlag?.enabled,
  };
};

export const useQueryLocation = (id?: string) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const offlineMode = useOfflineMode(id);
  const { meta } = offlineMode;

  const shouldUseCache = (data: LocationDtoV3, cachedData?: LocationDtoV3) => {
    if (!cachedData) {
      return false;
    }

    return cachedData.paymentMethods.length > data.paymentMethods.length;
  };

  return useQuery({
    queryKey: ['location', id],

    queryFn: async () => {
      try {
        const cachedData = queryClient.getQueryData<LocationDtoV3>(['location', id]);

        const data = await apiClient.LocationControllerApi.locationByIdv6(
          { id: id! } // eslint-disable-line @typescript-eslint/no-non-null-assertion
        );

        // Cache the locationId -> numeric id mapping for faster subsequent loads
        if (id && data.id) {
          setCachedNumericId(id, data.id);
        }

        const useCache = shouldUseCache(data, cachedData);

        return getOptimisticLocation(useCache, data, cachedData);
      } catch {
        return queryClient.getQueryData<LocationDtoV3>(['location', id]) ?? undefined;
      }
    },
    refetchOnMount: true,
    enabled: isValidLocationId(id),
    meta: meta,
  });
};
